import React, { useState } from 'react';
import { getStringValue } from '../utils/blockHelpers';

const MaxPrepsDisplay = ({ block }) => {
  const [selectedCategory, setSelectedCategory] = useState('PASSING');

  if (!block.gameData || !block.teamStats) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        backgroundColor: '#f5f5f5',
        border: '2px dashed #ccc',
        borderRadius: '8px',
        padding: '2rem',
        marginBottom: '1.5rem',
        textAlign: 'center',
        color: '#666'
      }}>
        <div>
          <h3 style={{ marginTop: 0, color: '#333' }}>MaxPreps Game Details</h3>
          <p>Loading game statistics and team comparison data...</p>
          <p style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
            Dynamic content will display with game selection, team stats, and top performers.
          </p>
        </div>
      </div>
    );
  }

  const getAvailableCategories = () => {
    return ['PASSING', 'RUSHING', 'RECEIVING', 'DEFENSE', 'KICKING', 'BLOCKING'].filter(
      cat => block.allStats?.[cat]?.length > 0
    );
  };

  const availableCategories = getAvailableCategories();

  if (availableCategories.length === 0 || !availableCategories.includes(selectedCategory)) {
    const firstAvailable = availableCategories[0];
    if (firstAvailable) {
      setSelectedCategory(firstAvailable);
    }
  }

  const getCategoryStats = () => {
    const categoryStats = block.allStats[selectedCategory] || [];
    const team1Players = categoryStats.filter(p => p.TEAM === block.teamStats.team1.name) || [];
    const team2Players = categoryStats.filter(p => p.TEAM === block.teamStats.team2.name) || [];

    const statKeys = Object.keys(categoryStats[0] || {})
      .filter(key => !['id', 'TEAM_ID', 'TEAM', 'NAME', 'NO', 'POS'].includes(key));

    return { team1Players, team2Players, statKeys };
  };

  const { team1Players, team2Players, statKeys } = getCategoryStats();

  const formatValue = (val) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'number') return val.toFixed(2);
    if (typeof val === 'object') return getStringValue(val);
    return val;
  };

  const getStatValue = (players, statKey) => {
    if (!players || players.length === 0) return '-';
    const value = players.reduce((sum, p) => sum + (parseFloat(p[statKey]) || 0), 0) / players.length;
    return isNaN(value) ? '-' : value.toFixed(2);
  };

  const getStatKey = (category) => {
    const keys = {
      'PASSING': 'YRDS',
      'RUSHING': 'YRDS',
      'RECEIVING': 'YRDS',
      'DEFENSE': 'TCK',
      'KICKING': 'PNTS',
      'BLOCKING': 'SNPS',
    };
    return keys[category] || 'YRDS';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'PASSING': 'Passing Yards',
      'RUSHING': 'Rushing Yards',
      'RECEIVING': 'Receiving Yards',
      'DEFENSE': 'Tackles',
      'KICKING': 'Points',
      'BLOCKING': 'Snaps',
    };
    return labels[category] || category;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'PASSING': '📊',
      'RUSHING': '🏈',
      'RECEIVING': '🎯',
      'DEFENSE': '🛡️',
      'KICKING': '🦶',
      'BLOCKING': '💪',
    };
    return icons[category] || '⚡';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'PASSING': '#0066ff',
      'RUSHING': '#00cc00',
      'RECEIVING': '#9933ff',
      'DEFENSE': '#ff6600',
      'KICKING': '#ffcc00',
      'BLOCKING': '#cc0000',
    };
    return colors[category] || '#003366';
  };

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <h4 style={{ marginTop: 0 }}>Final Score</h4>
        {block.gameData && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2rem', fontSize: '1.5rem', fontWeight: 'bold' }}>
            <div>{getStringValue(block.gameData.team1?.name)}</div>
            <div>{block.finalScore && block.finalScore !== '' ? block.finalScore : 'Game Scheduled'}</div>
            <div>{getStringValue(block.gameData.team2?.name)}</div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: '2rem' }}>
        {block.teamStats.team1 && block.teamStats.team2 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
            <div style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
              <h5 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>{getStringValue(block.teamStats.team1.name)}</h5>
              <div style={{ fontSize: '0.9rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem', borderBottom: '1px solid #ddd' }}>
                  <span>Wins:</span>
                  <strong>{block.teamStats.team1.stats?.wins || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem', borderBottom: '1px solid #ddd' }}>
                  <span>Losses:</span>
                  <strong>{block.teamStats.team1.stats?.losses || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem' }}>
                  <span>Points For:</span>
                  <strong>{block.teamStats.team1.stats?.points_for || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Points Against:</span>
                  <strong>{block.teamStats.team1.stats?.points_against || 0}</strong>
                </div>
              </div>
            </div>
            <div style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
              <h5 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>{getStringValue(block.teamStats.team2.name)}</h5>
              <div style={{ fontSize: '0.9rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem', borderBottom: '1px solid #ddd' }}>
                  <span>Wins:</span>
                  <strong>{block.teamStats.team2.stats?.wins || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem', borderBottom: '1px solid #ddd' }}>
                  <span>Losses:</span>
                  <strong>{block.teamStats.team2.stats?.losses || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.5rem' }}>
                  <span>Points For:</span>
                  <strong>{block.teamStats.team2.stats?.points_for || 0}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Points Against:</span>
                  <strong>{block.teamStats.team2.stats?.points_against || 0}</strong>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {block.allStats && availableCategories.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', backgroundColor: '#003366', gap: 0, marginBottom: '1rem', overflowX: 'auto' }}>
            {availableCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                style={{
                  flex: '1',
                  padding: '0.75rem',
                  backgroundColor: '#003366',
                  color: selectedCategory === cat ? '#fff' : '#ccc',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: selectedCategory === cat ? 'bold' : 'normal',
                  borderBottom: selectedCategory === cat ? '3px solid #00ff00' : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {statKeys.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                      {getStringValue(block.teamStats.team1.name)}
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd', width: '120px' }}>
                      STAT
                    </th>
                    <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #ddd' }}>
                      {getStringValue(block.teamStats.team2.name)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {statKeys.map((statKey) => {
                    const team1Value = getStatValue(team1Players, statKey);
                    const team2Value = getStatValue(team2Players, statKey);
                    const team1Num = parseFloat(team1Value);
                    const team2Num = parseFloat(team2Value);
                    const team1Winner = !isNaN(team1Num) && !isNaN(team2Num) && team1Num > team2Num;
                    const team2Winner = !isNaN(team1Num) && !isNaN(team2Num) && team2Num > team1Num;

                    return (
                      <tr key={statKey} style={{ borderBottom: '1px solid #eee' }}>
                        <td
                          style={{
                            padding: '0.75rem',
                            fontWeight: team1Winner ? 'bold' : 'normal',
                            color: team1Winner ? '#003366' : 'inherit'
                          }}
                        >
                          {formatValue(team1Value)}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>
                          {statKey}
                        </td>
                        <td
                          style={{
                            padding: '0.75rem',
                            textAlign: 'right',
                            fontWeight: team2Winner ? 'bold' : 'normal',
                            color: team2Winner ? '#003366' : 'inherit'
                          }}
                        >
                          {formatValue(team2Value)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {block.topPerformers && Object.keys(block.topPerformers).length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ 
            backgroundColor: '#f0f0f0', 
            padding: '2rem',
            borderRadius: '8px',
            borderLeft: '6px solid #ff9900'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '2rem', color: '#1a1a1a', fontSize: '1.75rem' }}>
              Top Performers
            </h3>
            
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '2rem',
              marginBottom: '2rem'
            }}>
              {[block.gameData.team1, block.gameData.team2].map((team, teamIndex) => (
                <div key={teamIndex}>
                  <h4 style={{ 
                    marginTop: 0, 
                    marginBottom: '1.5rem', 
                    color: '#1a1a1a',
                    textAlign: 'center',
                    fontSize: '1.1rem'
                  }}>
                    {getStringValue(team?.name)}
                  </h4>
                  
                  <div style={{ 
                    display: 'flex',
                    gap: '1rem',
                    flexDirection: 'column'
                  }}>
                    {Object.entries(block.topPerformers).map(([category, categoryData]) => {
                      const performer = teamIndex === 0 ? categoryData?.team1 : categoryData?.team2;
                      
                      if (!performer) {
                        return (
                          <div key={category} style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '1rem',
                            borderLeft: '5px solid ' + getCategoryColor(category),
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            opacity: 0.5
                          }}>
                            <div style={{
                              fontSize: '0.75rem',
                              color: '#999',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              fontWeight: '600',
                              marginBottom: '0.5rem'
                            }}>
                              {getCategoryLabel(category)}
                            </div>
                            <div style={{
                              color: '#999',
                              fontStyle: 'italic'
                            }}>
                              No data
                            </div>
                          </div>
                        );
                      }
                      
                      const playerName = getStringValue(performer.NAME) || 'N/A';
                      const statKey = getStatKey(category);
                      const statValue = performer[statKey] || '-';
                      const borderColor = getCategoryColor(category);
                      const icon = getCategoryIcon(category);
                      
                      return (
                        <div key={category} style={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          padding: '1.25rem',
                          borderLeft: '5px solid ' + borderColor,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '1rem'
                          }}>
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              backgroundColor: borderColor,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.25rem',
                              fontWeight: 'bold',
                              color: 'white',
                              flexShrink: 0
                            }}>
                              {icon}
                            </div>
                            
                            <div style={{ flex: 1 }}>
                              <div style={{
                                fontSize: '0.75rem',
                                color: '#999',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                fontWeight: '600',
                                marginBottom: '0.5rem'
                              }}>
                                {getCategoryLabel(category)}
                              </div>
                              
                              <div style={{
                                fontWeight: '700',
                                fontSize: '1rem',
                                color: '#1a1a1a',
                                marginBottom: '0.25rem'
                              }}>
                                {playerName}
                              </div>
                              
                              <div style={{
                                fontSize: '0.8rem',
                                color: '#666',
                                marginBottom: '0.5rem'
                              }}>
                                {performer.POS && getStringValue(performer.POS)}
                              </div>
                              
                              <div style={{
                                fontSize: '1.5rem',
                                fontWeight: '700',
                                color: borderColor
                              }}>
                                {formatValue(statValue)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaxPrepsDisplay;
